import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Lightbulb, Search, Save, X, Check, Image } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SuggestionsTab = ({ menuItems = [], categories = [] }) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/admin/suggestion-items`);
        setSelectedIds(res.data.item_ids || []);
      } catch (e) { console.error(e); }
      setLoaded(true);
    };
    load();
  }, []);

  const toggleItem = (itemId) => {
    setSelectedIds(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const saveSuggestions = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/suggestion-items`, { item_ids: selectedIds });
      alert('Suggestions sauvegardees !');
    } catch (e) {
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const activeItems = useMemo(() => menuItems.filter(i => i.is_active !== false), [menuItems]);

  const filtered = useMemo(() => {
    let items = activeItems;
    if (activeCategory !== 'all') {
      items = items.filter(i => (i.category_id || i.category) === activeCategory);
    }
    if (search) {
      items = items.filter(i => i.name.toLowerCase().includes(search.toLowerCase()));
    }
    return items;
  }, [activeItems, activeCategory, search]);

  const selectedItems = useMemo(() => activeItems.filter(i => selectedIds.includes(i.id)), [activeItems, selectedIds]);

  // Count items per category for the badges
  const catCounts = useMemo(() => {
    const counts = {};
    activeItems.forEach(i => {
      const catId = i.category_id || i.category || 'other';
      counts[catId] = (counts[catId] || 0) + 1;
    });
    return counts;
  }, [activeItems]);

  // Only show categories that have items
  const availableCategories = useMemo(() =>
    categories.filter(c => catCounts[c.id] > 0),
    [categories, catCounts]
  );

  if (!loaded) return <div className="text-center py-12 text-[#6E5E52]">Chargement...</div>;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3] flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-[#2D241D]" data-testid="suggestions-title">
            Suggestions Checkout ({selectedIds.length} articles)
          </h3>
          <p className="text-xs text-[#6E5E52]">
            Selectionnez les articles qui apparaitront comme suggestions avant le paiement client
          </p>
        </div>
        <Button onClick={saveSuggestions} disabled={saving}
          className="bg-[#0A422D] hover:bg-[#083323] text-white rounded-xl" data-testid="save-suggestions-btn">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Sauvegarde...' : 'Sauvegarder'}
        </Button>
      </div>

      {/* Selected items preview */}
      {selectedItems.length > 0 && (
        <div className="bg-[#FFF8E1] rounded-2xl p-4 border border-[#D4A853]/30">
          <p className="text-sm font-semibold text-[#2D241D] mb-3 flex items-center gap-2">
            <Lightbulb className="w-4 h-4 text-[#D4A853]" /> Articles selectionnes ({selectedItems.length})
          </p>
          <div className="flex flex-wrap gap-2">
            {selectedItems.map(item => (
              <button
                key={item.id}
                onClick={() => toggleItem(item.id)}
                className="flex items-center gap-2 bg-white rounded-xl px-3 py-2 border border-[#D4A853]/40 text-sm text-[#2D241D] hover:border-red-300 transition-colors"
                data-testid={`remove-suggestion-${item.id}`}
              >
                {item.image_url && <img src={item.image_url} alt="" className="w-6 h-6 rounded object-cover" />}
                <span>{item.name}</span>
                <X className="w-3 h-3 text-red-400" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Search + Category filter */}
      <div className="space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6E5E52]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un article..." className="pl-10 rounded-xl border-[#E5DCD3]"
            data-testid="suggestions-search" />
        </div>

        {/* Category tabs */}
        <div className="flex flex-wrap gap-2" data-testid="suggestions-category-filter">
          <button
            onClick={() => setActiveCategory('all')}
            className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-colors ${
              activeCategory === 'all'
                ? 'bg-[#0A422D] text-white'
                : 'bg-[#F3EDE5] text-[#6E5E52] hover:bg-[#E5DCD3]'
            }`}
            data-testid="suggestions-cat-all"
          >
            Tout ({activeItems.length})
          </button>
          {availableCategories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-colors ${
                activeCategory === cat.id
                  ? 'bg-[#0A422D] text-white'
                  : 'bg-[#F3EDE5] text-[#6E5E52] hover:bg-[#E5DCD3]'
              }`}
              data-testid={`suggestions-cat-${cat.id}`}
            >
              {cat.name_fr || cat.name} ({catCounts[cat.id] || 0})
            </button>
          ))}
        </div>
      </div>

      {/* Items grid */}
      <div className="grid gap-2 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
        {filtered.map(item => {
          const isSelected = selectedIds.includes(item.id);
          return (
            <button
              key={item.id}
              onClick={() => toggleItem(item.id)}
              className={`relative rounded-2xl border-2 p-2 text-left transition-all ${
                isSelected
                  ? 'border-[#D4A853] bg-[#FFF8E1]'
                  : 'border-[#E5DCD3] bg-white hover:border-[#D4A853]/50'
              }`}
              data-testid={`suggestion-item-${item.id}`}
            >
              {isSelected && (
                <div className="absolute top-1 right-1 w-5 h-5 bg-[#D4A853] rounded-full flex items-center justify-center">
                  <Check className="w-3 h-3 text-white" />
                </div>
              )}
              <div className="w-full h-16 rounded-xl overflow-hidden bg-[#F3EDE5] mb-2">
                {item.image_url ? (
                  <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Image className="w-6 h-6 text-[#E5DCD3]" />
                  </div>
                )}
              </div>
              <p className="text-xs font-semibold text-[#2D241D] line-clamp-1">{item.name}</p>
              <p className="text-[10px] text-[#E16833] font-bold">{item.price} THB</p>
            </button>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-8 text-[#6E5E52]">
          <Lightbulb className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p>Aucun article trouve</p>
        </div>
      )}
    </div>
  );
};
